import type { ContentItem, ToolCallBlock, TurnBlock } from './types.js';

/**
 * UI-only model for a launched agent's activity-bar row. This is derived
 * entirely client-side from Reflex MCP tool calls (see
 * {@link parseLaunchedAgents}); it is NOT a shared contract and must not leak
 * into `@reflex/shared`, the OpenAPI client, or server persistence. A later
 * version can replace this parsing with explicit server-side lifecycle events.
 */
export type LaunchedAgentStatus = 'working' | 'needs_input' | 'completed' | 'errored' | 'stopped';

/** One launched agent, keyed by its `agentId`, ready for the activity bar. */
export interface LaunchedAgentCardItem {
  agentId: string;
  /** Display name from the launch result, when the tool returned one. */
  name?: string;
  /** Harness that runs the agent (e.g. `claude-code`, `codex`), when known. */
  agentType?: string;
  /** Model id the agent was launched with (e.g. `claude-sonnet-4-20250514`). */
  model?: string;
  status: LaunchedAgentStatus;
  /** Completion summary surfaced once a `get_agent_result` closes the entry. */
  summary?: string;
  /** The pending question text while the launched agent waits for input. */
  question?: string;
  /** Launch tool `startedAt`, so the entry can anchor a timestamp. */
  startedAt: number;
  /** Duration (seconds) of the most recent `get_agent_result`, when known. */
  durationSecs?: number;
}

// Reflex MCP tools that spawn a new agent and return its id.
const LAUNCH_TOOLS: ReadonlySet<string> = new Set(['start_agent', 'launch_agent_persona']);
// Reflex MCP tools that update a launched agent's boundary.
const RESULT_TOOLS: ReadonlySet<string> = new Set([
  'get_agent_result',
  'get_agent_response',
  'complete_agent',
]);
// Reflex MCP tools that start a new turn on a launched agent: the previous
// summary/question no longer describe its live state.
const REOPEN_TOOLS: ReadonlySet<string> = new Set(['send_agent_message', 'answer_agent_question']);

/**
 * Strip a runtime `mcp__<server>__` prefix so `mcp__reflex__start_agent` and
 * the already-normalized `start_agent` both resolve to the same tool key. Tool
 * names use single underscores internally and double underscores as segment
 * separators, so the final segment is the bare tool name.
 *
 * Exported for `ToolCallBlockView`, which uses the same key to look up
 * plugin-contributed tool-call renderers.
 */
export function normalizeToolName(name: string): string {
  const segments = name.split('__');
  return segments[segments.length - 1] ?? name;
}

export interface McpToolName {
  server: string;
  tool: string;
}

/**
 * Codex names an MCP call `<server>: <tool>` in the title rather than using
 * Claude's `mcp__` prefix, so without this those rows would keep the raw title
 * and lose the arguments panel the prefixed ones get.
 *
 * Kept deliberately tight, because a title is free text and a native tool
 * could contain a colon: both halves must be a single bare identifier-ish word
 * (letters, digits, `_`, `-`, `.`) and the separator must be exactly `": "`.
 * A title with spaces on either side — the shape of prose like
 * `Read: config file` — does not match.
 */
const CODEX_MCP_TITLE = /^([A-Za-z0-9][\w.-]*): ([A-Za-z0-9][\w.-]*)$/;

/**
 * Parse a tool name into its MCP server and tool parts, so a call can render
 * as `<server>:<tool>` with its arguments visible instead of the raw name.
 * Handles both wire forms: Claude's `mcp__<server>__<tool>` and Codex's
 * `<server>: <tool>` title.
 *
 * Returns null for anything that is neither, including a name already reduced
 * to its bare tool segment by {@link normalizeToolName}. A tool name with more
 * than one `__`-separated segment after the server (rare) joins the extras
 * with `:` rather than leaving the double-underscore noise this exists to
 * remove — note that {@link normalizeToolName} still keys plugin renderers off
 * only the last segment, so the two can diverge for such names.
 */
export function parseMcpToolName(name: string): McpToolName | null {
  const segments = name.split('__');
  if (segments.length >= 3 && segments[0] === 'mcp') {
    const server = segments[1];
    const tool = segments.slice(2).join(':');
    if (server && tool) return { server, tool };
    return null;
  }
  const codex = CODEX_MCP_TITLE.exec(name);
  return codex ? { server: codex[1], tool: codex[2] } : null;
}

function toolNameOf(block: ToolCallBlock): string | null {
  const fromExtra = block.extra?.toolName;
  if (typeof fromExtra === 'string' && fromExtra.length > 0) return normalizeToolName(fromExtra);
  if (typeof block.title === 'string' && block.title.length > 0)
    return normalizeToolName(block.title);
  return null;
}

/** Join the textual content items of a completed tool call. */
function contentText(items: ContentItem[]): string | undefined {
  const texts = items.filter((c) => c.type === 'content' && c.text).map((c) => c.text as string);
  return texts.length > 0 ? texts.join('\n') : undefined;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function parseJsonRecord(text: string): Record<string, unknown> | null {
  try {
    return asRecord(JSON.parse(text));
  } catch {
    return null;
  }
}

function readNestedOutputObject(output: Record<string, unknown>): Record<string, unknown> | null {
  for (const key of ['output', 'stdout', 'text']) {
    const value = output[key];
    if (typeof value !== 'string' || value.trim().length === 0) continue;
    const parsed = parseJsonRecord(value);
    if (parsed) return parsed;
  }

  const content = output.content;
  if (Array.isArray(content)) {
    for (const entry of content) {
      const record = asRecord(entry);
      if (!record) continue;
      let text: string | null = null;
      if (typeof record.text === 'string') {
        text = record.text;
      } else {
        const nested = asRecord(record.content);
        if (nested?.type === 'text' && typeof nested.text === 'string') {
          text = nested.text;
        }
      }
      if (!text) continue;
      const parsed = parseJsonRecord(text);
      if (parsed) return parsed;
    }
  }

  return null;
}

/**
 * Read a tool result as a JSON object. Reflex MCP tools serialize their result
 * as JSON text in the tool_result content, so that is preferred; `rawOutput`
 * is a fallback for protocols that populate a structured object instead.
 * Returns `null` when the payload is missing or not a JSON object, so callers
 * fail closed to the existing tool-call row.
 */
function readResultObject(block: ToolCallBlock): Record<string, unknown> | null {
  const text = contentText(block.content);
  if (text) {
    const parsed = parseJsonRecord(text);
    if (parsed) return parsed;
  }
  const rawOutput = asRecord(block.rawOutput);
  if (!rawOutput) return null;
  return readNestedOutputObject(rawOutput) ?? rawOutput;
}

function readAgentId(result: Record<string, unknown>): string | undefined {
  const id = result.id ?? result.agentId;
  return typeof id === 'string' && id.length > 0 ? id : undefined;
}

function readName(result: Record<string, unknown>): string | undefined {
  return typeof result.name === 'string' && result.name.length > 0 ? result.name : undefined;
}

/**
 * Copy the harness (`agentType`) and `model` off a payload that carries them.
 * Launch tools return them up front; `get_agent_result` also echoes them so a
 * card whose launch predates that payload backfills on the next poll.
 */
function applyAgentConfig(card: LaunchedAgentCardItem, result: Record<string, unknown>): void {
  if (typeof result.agentType === 'string' && result.agentType.length > 0) {
    card.agentType = result.agentType;
  }
  if (typeof result.model === 'string' && result.model.length > 0) {
    card.model = result.model;
  }
}

function readSummary(result: Record<string, unknown>): string | undefined {
  // `response` is get_agent_response's reply text; the others come from
  // get_agent_result / complete_agent payloads.
  const summary = result.summary ?? result.response ?? result.lastMessage;
  return typeof summary === 'string' && summary.trim().length > 0 ? summary.trim() : undefined;
}

/**
 * Whether a result payload proves a turn has finished. Only the `summary` key
 * qualifies: the server derives it from a turn-end event, whereas
 * `lastMessage` streams while a turn is still in flight.
 */
function hasTurnEndSummary(result: Record<string, unknown>): boolean {
  return typeof result.summary === 'string' && result.summary.trim().length > 0;
}

/**
 * Map a raw agent status onto the card's coarse state. `running`/`starting`
 * (and any unknown status) read as still working; `needs_input` is rendered
 * distinctly; everything terminal collapses to completed / errored / stopped.
 * Accepts both the canonical `AgentStatus` values and the looser synonyms
 * (`errored`, `failed`, `cancelled`) a result payload might carry.
 */
function mapStatus(raw: string): LaunchedAgentStatus {
  switch (raw) {
    case 'completed':
      return 'completed';
    case 'error':
    case 'errored':
    case 'failed':
      return 'errored';
    case 'stopped':
    case 'stopping':
    case 'terminated':
    case 'interrupted':
    case 'cancelled':
    case 'canceled':
      return 'stopped';
    case 'needs_input':
      return 'needs_input';
    default:
      return 'working';
  }
}

/**
 * Derive launched-agent entries from an ordered run of turn blocks (finalized
 * assistant messages followed by the live turn). A launch tool result opens a
 * working entry keyed by the returned agent id; a matching result/lifecycle
 * tool updates that entry's status and summary, and a follow-up message
 * reopens the entry for the next turn. Unknown tools, malformed results, and
 * results for agents we never saw launch are ignored so the existing
 * tool-call rows remain the fallback. The latest result per agent wins.
 */
export function parseLaunchedAgents(blocks: readonly TurnBlock[]): LaunchedAgentCardItem[] {
  const byId = new Map<string, LaunchedAgentCardItem>();
  const order: string[] = [];

  for (const block of blocks) {
    if (block.type !== 'tool_call') continue;
    // A tool result is required to derive any card state, and interrupted or
    // failed launches never returned an id to open a card with.
    if (block.status !== 'completed') continue;

    const name = toolNameOf(block);
    if (!name) continue;

    if (LAUNCH_TOOLS.has(name)) {
      const result = readResultObject(block);
      if (!result) continue;
      const agentId = readAgentId(result);
      if (!agentId) continue;

      let card = byId.get(agentId);
      if (!card) {
        card = { agentId, status: 'working', startedAt: block.startedAt };
        byId.set(agentId, card);
        order.push(agentId);
        // Only the opening launch seeds status/name — a later get_agent_result
        // owns the card's live status and must not be clobbered by a replayed
        // launch block for the same id.
        if (typeof result.status === 'string') card.status = mapStatus(result.status);
      }
      const name_ = readName(result);
      if (name_) card.name = name_;
      applyAgentConfig(card, result);
      continue;
    }

    if (RESULT_TOOLS.has(name)) {
      const result = readResultObject(block);
      if (!result) continue;
      const agentId = readAgentId(result);
      if (!agentId) continue;
      const card = byId.get(agentId);
      if (!card) continue; // result for an agent we never saw launch — ignore

      applyAgentConfig(card, result);
      if (typeof result.status === 'string') card.status = mapStatus(result.status);
      // A Reflex agent's status stays `running` after a natural turn-end (only
      // an explicit complete_agent flips it to `completed`), so a still-working
      // status alongside a turn-end summary means the drone has reported back.
      if (card.status === 'working' && hasTurnEndSummary(result)) card.status = 'completed';
      const summary = readSummary(result);
      if (summary) card.summary = summary;
      // A pending question is only meaningful while the agent is blocked.
      card.question =
        card.status === 'needs_input' && typeof result.question === 'string'
          ? result.question
          : undefined;
      if (typeof block.duration === 'number') card.durationSecs = block.duration;
      continue;
    }

    if (REOPEN_TOOLS.has(name)) {
      const result = readResultObject(block);
      if (!result || result.ok !== true) continue;
      const agentId = readAgentId(result);
      if (!agentId) continue;
      const card = byId.get(agentId);
      if (!card) continue;

      // A new turn is starting: the stored summary/question described the
      // previous turn and would otherwise read the card as already done.
      card.status = typeof result.status === 'string' ? mapStatus(result.status) : 'working';
      card.summary = undefined;
      card.question = undefined;
    }
  }

  return order.map((id) => byId.get(id) as LaunchedAgentCardItem);
}
